import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { CircularProgress, Box } from "@mui/material";

export default function Vendors({ dashboardData, setDashboardData }) {
  const pieChartRef = useRef(null);
  const revenueChartRef = useRef(null);
  const salesChartRef = useRef(null);
  const chartInstancesRef = useRef({});
  const [activeAccordion, setActiveAccordion] = useState(null);

  const handleAccordionClick = (aggregator) =>
    setActiveAccordion(activeAccordion === aggregator ? null : aggregator);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);

  const getTodayMetrics = (aggregatorName) =>
    dashboardData?.aggregatorMetrics?.today?.find(
      (agg) => agg.aggregator === aggregatorName
    ) || null;

  const getAllTimeMetrics = (aggregatorName) =>
    dashboardData?.aggregatorMetrics?.allTime?.find(
      (agg) => agg.aggregator === aggregatorName
    ) || null;

  // eslint-disable-next-line no-unused-vars
  const calculateGrowth = (opening, closing) => {
    if (!opening || !closing) return 0;
    return ((closing - opening) / opening) * 100;
  };

  useEffect(() => {
    const cleanup = () => {
      Object.values(chartInstancesRef.current).forEach((chart) =>
        chart?.destroy()
      );
    };

    if (dashboardData?.aggregatorMetrics?.allTime) {
      cleanup();

      const aggregators = dashboardData.aggregatorMetrics.allTime;
      const labels = aggregators.map((agg) => agg.aggregator);
      const orderData = aggregators.map((agg) => agg.totalOrders);
      const revenueData = aggregators.map((agg) => agg.totalRevenue);
      const avgOrderData = aggregators.map((agg) => agg.averageOrderValue);

      // Pie Chart
      if (pieChartRef.current) {
        const ctx = pieChartRef.current.getContext("2d");
        chartInstancesRef.current.pie = new Chart(ctx, {
          type: "pie",
          data: {
            labels,
            datasets: [
              {
                data: orderData,
                backgroundColor: [
                  "#4e73df",
                  "#1cc88a",
                  "#36b9cc",
                  "#f6c23e",
                  "#e74a3b",
                ],
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: false,
              },
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 12,
                  padding: 10,
                  font: {
                    size: 11
                  }
                }
              },
            },
          },
        });
      }

      // Revenue Chart
      if (revenueChartRef.current) {
        const ctx = revenueChartRef.current.getContext("2d");
        chartInstancesRef.current.revenue = new Chart(ctx, {
          type: "line",
          data: {
            labels,
            datasets: [
              {
                label: "Total Revenue",
                data: revenueData,
                borderColor: "#4e73df",
                tension: 0.1,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { 
                  callback: (v) => formatCurrency(v),
                  font: {
                    size: 10
                  }
                },
              },
              x: {
                ticks: {
                  font: {
                    size: 10
                  }
                }
              }
            },
          },
        });
      }

      // Sales Performance Chart
      if (salesChartRef.current) {
        const ctx = salesChartRef.current.getContext("2d");
        chartInstancesRef.current.sales = new Chart(ctx, {
          type: "bar",
          data: {
            labels,
            datasets: [
              {
                label: "Average Order Value",
                data: avgOrderData,
                backgroundColor: "#1cc88a",
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { 
                  callback: (v) => formatCurrency(v),
                  font: {
                    size: 10
                  }
                },
              },
              x: {
                ticks: {
                  font: {
                    size: 10
                  }
                }
              }
            },
          },
        });
      }
    }

    return cleanup;
  }, [dashboardData]);

  // Show loader if data is not ready
  if (!dashboardData?.aggregatorMetrics) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="vendors-container">
      {/* Overview Section - Left Side */}
      <div className="vendors-overview-section">
        <h3 className="section-title">Overview</h3>
        <div className="overview-content">
          {dashboardData.aggregatorMetrics.allTime.map((aggregator) => {
            const todayMetrics = getTodayMetrics(aggregator.aggregator);
            const allTimeMetrics = getAllTimeMetrics(aggregator.aggregator);

            return (
              <div
                key={aggregator.aggregator}
                className={`accordion-item ${
                  activeAccordion === aggregator.aggregator ? "active" : ""
                }`}
              >
                <div
                  className="accordion-header"
                  onClick={() => handleAccordionClick(aggregator.aggregator)}
                >
                  <div className="header-content">
                    <h3>{aggregator.aggregator}</h3>
                  </div>
                </div>
                <div className="accordion-content">
                  <div className="metrics-section">
                    <h4>Today's Performance</h4>
                    <div className="stats-grid">
                      <div className="stat-card vendor-stat-card">
                        <h4>Orders</h4>
                        <p>{todayMetrics?.orderCount || 0}</p>
                      </div>
                      <div className="stat-card vendor-stat-card">
                        <h4>Revenue</h4>
                        <p>{formatCurrency(todayMetrics?.revenue || 0)}</p>
                      </div>
                      <div className="stat-card vendor-stat-card">
                        <h4>Opening Amount</h4>
                        <p>{formatCurrency(todayMetrics?.openingAmount || 0)}</p>
                      </div>
                      <div className="stat-card vendor-stat-card">
                        <h4>Closing Amount</h4>
                        <p>{formatCurrency(todayMetrics?.closingAmount || 0)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="metrics-section">
                    <h4>All-Time Statistics</h4>
                    <div className="stats-grid">
                      <div className="stat-card vendor-stat-card">
                        <h4>Total Orders</h4>
                        <p>{allTimeMetrics?.totalOrders || 0}</p>
                      </div>
                      <div className="stat-card vendor-stat-card">
                        <h4>Total Revenue</h4>
                        <p>{formatCurrency(allTimeMetrics?.totalRevenue || 0)}</p>
                      </div>
                      <div className="stat-card vendor-stat-card">
                        <h4>Average Order Value</h4>
                        <p>
                          {formatCurrency(allTimeMetrics?.averageOrderValue || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Analysis Section - Right Side */}
      <div className="vendors-analysis-section">
        <h3 className="section-title">Analysis</h3>
        <div className="analysis-content">
          <div className="analysis-grid">
            <div className="card analysis-card">
              <h5 className="card-title">Order Distribution</h5>
              <div className="chart-container">
                <canvas ref={pieChartRef}></canvas>
              </div>
            </div>

            <div className="card analysis-card">
              <h5 className="card-title">Revenue Trends</h5>
              <div className="chart-container">
                <canvas ref={revenueChartRef}></canvas>
              </div>
            </div>

            <div className="card analysis-card">
              <h5 className="card-title">Average Order Values</h5>
              <div className="chart-container">
                <canvas ref={salesChartRef}></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
